/**
 * Step 4 — Favorite Location (optional interstitial)
 *
 * Shown once after the 3 numbered steps, before landing on Home. Captures a
 * single favorite location so the user gets alerts when rides/parcels are
 * posted nearby — the app's main re-engagement hook. Deliberately minimal:
 * just an address (or "use my current location") + Skip. Radius and the
 * notify/match preferences ship with sensible defaults (tunable later in
 * Settings → Location Alerts); the label is auto-derived from the city.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import Spinner from '../common/Spinner';
import AddFavoriteLocationInline from './AddFavoriteLocationInline';
import { logProfileAction, logProfileError } from '../../utils/errorLogger';

const Step4FavoriteLocation = ({ userData, onComplete }) => {
  const { t } = useTranslation('common');
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasLocation = !!(location && location.latitude && location.longitude);

  const handleContinue = async () => {
    setLoading(true);
    try {
      if (hasLocation) {
        // Never write an empty label — the Settings location list calls
        // label.toLowerCase() on it. Fall back to the city, then "Home".
        const label =
          (location.label && location.label.trim()) ||
          (location.city && location.city.trim()) ||
          'Home';

        const { error } = await supabase.from('user_favorite_locations').insert({
          user_id: userData.id,
          label,
          address: location.address || label,
          city: location.city || null,
          latitude: location.latitude,
          longitude: location.longitude,
          place_id: location.place_id || null,
          radius_km: location.radius_km || 10,
          notify_for_rides: location.notify_for_rides !== false,
          notify_for_packages: location.notify_for_packages !== false,
          match_start_point: location.match_start_point !== false,
          match_end_point: location.match_end_point !== false,
          is_active: true
        });

        if (error) {
          logProfileError(error, 'step4', { action: 'save_favorite_location' });
        } else {
          logProfileAction('favorite_location_saved', 'step4', { hasCity: !!location.city });
        }
      }
    } catch (err) {
      logProfileError(err, 'step4', { action: 'save_favorite_location_exception' });
    } finally {
      setLoading(false);
      onComplete();
    }
  };

  const handleSkip = () => {
    logProfileAction('favorite_location_skipped', 'step4', {});
    onComplete();
  };

  return (
    <div className="pcf-form-step">
      <div className="pcf-content-container pcf-location-step">
        <div className="pcf-location-hero">
          <div className="pcf-location-icon">
            <FaMapMarkerAlt />
          </div>
          <h3>{t('profile_setup.location_title')}</h3>
          <p>{t('profile_setup.location_subtitle')}</p>
        </div>

        <AddFavoriteLocationInline
          minimal
          showLabel={false}
          onLocationChange={setLocation}
          disabled={loading}
        />

        <div className="pcf-location-actions">
          <button
            type="button"
            className="enhanced-profile-button primary"
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? (
              <div className="button-label-with-spinner">
                <Spinner size="small" showText={false} />
                <span>{t('app.loading')}</span>
              </div>
            ) : hasLocation ? (
              t('profile_setup.location_continue')
            ) : (
              t('profile_setup.location_finish')
            )}
          </button>
          <button
            type="button"
            className="pcf-skip-link"
            onClick={handleSkip}
            disabled={loading}
          >
            {t('profile_setup.location_skip')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step4FavoriteLocation;
